import { GalleryRemoveLinear } from '@assessprep/icons/solar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const POLL_INTERVAL_MS = 5000;
const MAX_RETRIES = 24; // 24 * 5s = 2 minutes max
const ESTIMATED_DURATION_MS = 30000; // 30s estimated generation time
const PROGRESS_TICK_MS = 500;

// Hand-rolled circular progress (antd-free — this file is imported by the v4
// kit's APRenderHtml, so it must not pull antd into v4 bundles).
const RING_SIZE = 64;
const RING_STROKE = 6;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ percent }: { percent: number }) => (
  <div style={{ position: 'relative', width: RING_SIZE, height: RING_SIZE }}>
    <svg
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke="#f0f0f0"
        strokeWidth={RING_STROKE}
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke="#1890ff"
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * (1 - percent / 100)}
        transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
      />
    </svg>
    <span
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        color: '#595959',
      }}
    >
      {percent}%
    </span>
  </div>
);

interface AIImageProps {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onLoaded?: () => void;
}

/**
 * Image component that polls for AI-generated images.
 * Shows a progress indicator while the image is being generated in the background,
 * retrying every 5s with cache-busting to bypass CDN-cached 404s.
 */
export const AIImage = ({
  src,
  alt = 'Generated image',
  className,
  style,
  onLoaded,
}: AIImageProps) => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const [progress, setProgress] = useState(0);
  const retryCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(Date.now());

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const tryLoad = useCallback(() => {
    const img = new window.Image();
    // First attempt: use clean URL (CDN cache hit for already-uploaded images)
    // Retries: add cache-busting param to bypass CDN-cached 404s
    img.src = retryCount.current === 0 ? src : `${src}?v=${Date.now()}`;
    img.onload = () => {
      setLoaded(true);
      setGaveUp(false);
      setProgress(100);
      onLoadedRef.current?.();
    };
    img.onerror = () => {
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        timerRef.current = setTimeout(tryLoad, POLL_INTERVAL_MS);
      } else {
        setGaveUp(true);
      }
    };
  }, [src]);

  // Virtual progress: ease toward 95% over ~30s, never reaches 100 until loaded
  useEffect(() => {
    startTimeRef.current = Date.now();
    setProgress(0);

    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      // Asymptotic curve: approaches 95% but never reaches it
      const pct = Math.min(95, Math.round((elapsed / ESTIMATED_DURATION_MS) * 85 + 5));
      setProgress(pct);
    }, PROGRESS_TICK_MS);

    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [src]);

  useEffect(() => {
    setLoaded(false);
    setGaveUp(false);
    retryCount.current = 0;

    tryLoad();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [tryLoad]);

  // Clean up progress interval when loaded or gave up
  useEffect(() => {
    if (loaded || gaveUp) {
      if (progressRef.current) clearInterval(progressRef.current);
    }
  }, [loaded, gaveUp]);

  // U58/U62: once we give up, the URL is no longer pending — clear it from the
  // pending set so nothing downstream treats it as still-loading.
  useEffect(() => {
    if (gaveUp) onLoadedRef.current?.();
  }, [gaveUp]);

  const handleRetry = () => {
    retryCount.current = 0;
    setGaveUp(false);
    setLoaded(false);
    setProgress(0);
    startTimeRef.current = Date.now();
    tryLoad();
  };

  // U58/U62: a permanently-failed image degrades to a broken-image placeholder
  // with a retry — never a blank hole, and never a gate on adding the question.
  if (gaveUp) {
    return (
      <div
        style={{
          width: 300,
          height: 200,
          background: '#f5f5f5',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          ...style,
        }}
        data-testid="ai-image-failed"
      >
        <GalleryRemoveLinear aria-hidden="true" width={32} height={32} color="#bfbfbf" />
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>{t('AIImage.image_unavailable')}</span>
        <button
          type="button"
          onClick={handleRetry}
          style={{
            fontSize: 12,
            color: '#1890ff',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
          data-testid="ai-image-retry"
        >
          {t('AIImage.retry')}
        </button>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div
        style={{
          width: 300,
          height: 200,
          background: '#f5f5f5',
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          ...style,
        }}
      >
        <ProgressRing percent={progress} />
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>{t('AIImage.generating_image')}</span>
      </div>
    );
  }

  // Use clean URL (no cache-busting) for the final rendered image
  return <img src={src} alt={alt} className={className} style={style} />;
};
